import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { adminApi, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Search, Trash2, Pencil, X, Save, Phone, Mail, Users } from "lucide-react";

const EMPTY = { stage_name: "", first_name: "", last_name: "", specialty: "", phone: "", email: "", tariff_indicative: "", notes: "" };

export default function AdminPrestataires() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // holds prestataire being edited OR "new"
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi.listPrestataires()
      .then(setItems)
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => `${p.name} ${p.stage_name || ""} ${p.first_name || ""} ${p.last_name || ""} ${p.specialty} ${p.email} ${p.phone}`.toLowerCase().includes(q));
  }, [items, query]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (p) => {
    setForm({
      stage_name: p.stage_name || "",
      first_name: p.first_name || "",
      last_name: p.last_name || "",
      specialty: p.specialty || "",
      phone: p.phone || "",
      email: p.email || "",
      tariff_indicative: p.tariff_indicative ?? "",
      notes: p.notes || "",
    });
    setEditing(p);
  };
  const save = async (e) => {
    e.preventDefault();
    const hasName = form.stage_name.trim() || form.first_name.trim() || form.last_name.trim();
    if (!hasName) { toast.error("Renseignez un nom d'artiste ou un prénom/nom."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tariff_indicative: form.tariff_indicative === "" ? null : Number(form.tariff_indicative),
      };
      if (editing === "new") {
        await adminApi.createPrestataire(payload);
        toast.success("Prestataire ajouté.");
      } else {
        await adminApi.updatePrestataire(editing.id, payload);
        toast.success("Prestataire mis à jour.");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };
  const del = async (p) => {
    if (!window.confirm(`Supprimer ${p.name} du répertoire ?\n(Les assignations existantes sur les dossiers seront conservées.)`)) return;
    try {
      await adminApi.deletePrestataire(p.id);
      toast.success("Prestataire supprimé.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-prestataires">
      <header className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Répertoire</p>
          <h1 className="font-serif-luxe text-4xl md:text-5xl mt-2">
            Mes <span className="gold-text italic">prestataires.</span>
          </h1>
          <p className="text-white/55 text-sm mt-2">
            Vos contacts de confiance (saxophoniste, photographe, traiteur…), assignables directement sur chaque dossier client.
          </p>
        </div>
        <button type="button" onClick={openNew} data-testid="btn-new-prestataire" className="btn-gold inline-flex items-center gap-2">
          <UserPlus size={14} strokeWidth={1.4} /> Nouveau prestataire
        </button>
      </header>

      <div className="mb-5 flex items-center gap-3">
        <div className="flex items-center gap-2 border border-[#DCB457]/25 bg-black/60 px-3 py-2 flex-1 max-w-md focus-within:border-[#DCB457]">
          <Search size={13} strokeWidth={1.5} className="text-[#DCB457]/70 shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher par nom, spécialité, email…"
            data-testid="search-prestataires"
            className="bg-transparent text-sm outline-none flex-1 placeholder:text-white/40"
          />
        </div>
        <span className="text-xs text-white/45">{filtered.length} / {items.length}</span>
      </div>

      <div className="card-luxe overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-white/50 italic">Chargement…</p>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Users size={30} strokeWidth={1} className="text-[#DCB457] mx-auto mb-3" />
            <p className="text-white/55">{items.length === 0 ? "Aucun prestataire dans votre répertoire." : "Aucun résultat."}</p>
            {items.length === 0 && (
              <button type="button" onClick={openNew} className="btn-ghost-gold mt-6 inline-flex text-sm">Ajouter le premier prestataire</button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-[10px] uppercase tracking-[0.2em] text-white/55">
              <tr>
                <th className="px-3 py-3 text-left">Nom</th>
                <th className="px-3 py-3 text-left">Spécialité</th>
                <th className="px-3 py-3 text-left">Contact</th>
                <th className="px-3 py-3 text-right">Tarif indicatif</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-[#DCB457]/10" data-testid={`prestataire-row-${p.id}`}>
                  <td className="px-3 py-3">
                    <div className="text-white/90 font-medium">{p.stage_name || p.name}</div>
                    {(p.first_name || p.last_name) && (
                      <div className="text-[11px] text-white/50 mt-0.5">{[p.first_name, p.last_name].filter(Boolean).join(" ")}</div>
                    )}
                    {p.notes && <div className="text-[11px] text-white/45 mt-0.5 line-clamp-2">{p.notes}</div>}
                  </td>
                  <td className="px-3 py-3 text-white/70 text-xs">{p.specialty || "—"}</td>
                  <td className="px-3 py-3 text-xs">
                    {p.phone && (<div className="flex items-center gap-1.5 text-white/70"><Phone size={10} strokeWidth={1.5} />{p.phone}</div>)}
                    {p.email && (<div className="flex items-center gap-1.5 text-white/70 mt-0.5"><Mail size={10} strokeWidth={1.5} />{p.email}</div>)}
                    {!p.phone && !p.email && <span className="text-white/30 italic">—</span>}
                  </td>
                  <td className="px-3 py-3 text-right font-medium text-[#DCB457] whitespace-nowrap">
                    {p.tariff_indicative != null ? `${Number(p.tariff_indicative).toLocaleString("fr-FR")} €` : "—"}
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => openEdit(p)} title="Modifier" data-testid={`edit-prestataire-${p.id}`} className="inline-flex items-center text-white/70 hover:bg-white/5 px-1.5 py-1.5 border border-white/15 text-xs">
                        <Pencil size={11} strokeWidth={1.4} />
                      </button>
                      <button onClick={() => del(p)} title="Supprimer" data-testid={`del-prestataire-${p.id}`} className="inline-flex items-center text-red-300 hover:bg-red-500/10 px-1.5 py-1.5 border border-red-400/30 text-xs">
                        <Trash2 size={11} strokeWidth={1.4} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={() => !saving && setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#0a0a0a] border border-[#DCB457]/30 p-6" data-testid="prestataire-modal">
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">{editing === "new" ? "Nouveau prestataire" : "Modifier prestataire"}</p>
              <button type="button" onClick={() => setEditing(null)} disabled={saving} className="text-white/50 hover:text-white">
                <X size={14} strokeWidth={1.5} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Nom d'artiste</label>
                <input data-testid="form-prestataire-stagename" type="text" value={form.stage_name} onChange={(e) => setForm({...form, stage_name: e.target.value})} className="input-luxe mt-1 w-full" placeholder="Ex : Bruno Sax" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Prénom</label>
                <input data-testid="form-prestataire-firstname" type="text" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} className="input-luxe mt-1 w-full" placeholder="Bruno" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Nom</label>
                <input data-testid="form-prestataire-lastname" type="text" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} className="input-luxe mt-1 w-full" placeholder="Martin" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Spécialité</label>
                <input data-testid="form-prestataire-specialty" type="text" value={form.specialty} onChange={(e) => setForm({...form, specialty: e.target.value})} className="input-luxe mt-1 w-full" placeholder="Saxophoniste, Photographe…" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Tarif indicatif (€)</label>
                <input type="number" step="0.01" value={form.tariff_indicative} onChange={(e) => setForm({...form, tariff_indicative: e.target.value})} className="input-luxe mt-1 w-full" placeholder="350" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Téléphone</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="input-luxe mt-1 w-full" placeholder="06 XX XX XX XX" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="input-luxe mt-1 w-full" placeholder="contact@..." />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="input-luxe mt-1 w-full min-h-[70px]" placeholder="Disponibilités, préférences, remarques…" />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button type="submit" disabled={saving || !(form.stage_name.trim() || form.first_name.trim() || form.last_name.trim())} data-testid="submit-prestataire" className="btn-gold flex-1 py-2 text-sm disabled:opacity-60 inline-flex items-center justify-center gap-2">
                <Save size={13} strokeWidth={1.5} /> {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
              <button type="button" onClick={() => setEditing(null)} disabled={saving} className="border border-white/20 text-white/65 hover:bg-white/5 px-4 py-2 text-sm">Annuler</button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </div>
  );
}
